import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../auth/Auth';
import { cancelTenantTransaction } from '../../auth/_requests';
import { ButtonSpinner } from '../UI/ButtonSpinner/ButtonSpinner';
import { showToast } from '../utils/showToast';
import { isPendingTransaction } from '../utils/transactionDisplay';

const CANCEL_ERROR_MESSAGES: Record<string, string> = {
  tenant_transaction_not_cancellable:
    'This payment cannot be cancelled. It may already be processing or completed.',
  tenant_transaction_not_found: 'Transaction not found.',
};

const getCancelErrorMessage = (error: unknown): string => {
  const raw = error instanceof Error ? error.message : 'Failed to cancel payment';
  return CANCEL_ERROR_MESSAGES[raw] ?? raw;
};

export interface CancelPendingTransactionActionProps {
  tenantTransactionId: string | null;
  status: string;
  onCancelled?: () => void;
  className?: string;
}

export const CancelPendingTransactionAction: React.FC<CancelPendingTransactionActionProps> = ({
  tenantTransactionId,
  status,
  onCancelled,
  className,
}) => {
  const { auth } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  if (!tenantTransactionId || !isPendingTransaction(status)) {
    return null;
  }

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await cancelTenantTransaction(auth?.data?.token, tenantTransactionId);
      showToast({ message: 'Payment cancelled successfully', type: 'success' });
      setConfirming(false);
      onCancelled?.();
    } catch (error) {
      showToast({ message: getCancelErrorMessage(error), type: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  if (confirming) {
    return (
      <div
        className={`flex flex-col gap-4 rounded-[12px] border border-[#FDE68A] bg-[#FFFBEB] p-4 ${className ?? ''}`}
      >
        <div className='flex items-start gap-3'>
          <AlertTriangle className='mt-0.5 shrink-0 text-[#F59E0B]' size={20} />
          <p className='text-sm text-main'>
            Cancel this pending payment? Any authorization hold will be released.
          </p>
        </div>
        <div className='flex gap-3'>
          <button
            type='button'
            className='cancelBtn flex-1'
            disabled={cancelling}
            onClick={() => setConfirming(false)}
          >
            Keep payment
          </button>
          <button
            type='button'
            className='submitBtn flex-1 !bg-[#B70D0D] hover:!bg-[#9a0b0b]'
            disabled={cancelling}
            onClick={() => void handleCancel()}
          >
            {cancelling ? <ButtonSpinner /> : 'Confirm cancel'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type='button'
      className={`text-sm font-semibold text-[#B70D0D] hover:underline ${className ?? ''}`}
      onClick={() => setConfirming(true)}
    >
      Cancel payment
    </button>
  );
};
